import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-notice-box/PdNoticeBox.ts
/**
* @license
* Copyright (c) 2024 PD Progressive Development UG. All rights reserved.
*/
/**
* Default icon mapping for each notice type.
*/
var DEFAULT_ICONS = {
	info: pdIcons.ICON_INFO_FLD,
	note: pdIcons.ICON_INFO_FLD,
	success: pdIcons.ICON_CHECKBOX,
	warning: pdIcons.ICON_WARNING_FLD,
	error: pdIcons.ICON_ERROR_FLD
};
/**
* Notice box component for displaying contextual messages.
*
* Supports three visual variants:
* - **box**: Full-width box with icon, colored background (like Confluence info boxes)
* - **sidebar**: Minimal variant with colored left border only
* - **compact**: Inline-optimized variant with small icon, subtle tint, and reduced spacing — designed for embedding in other components
*
* @tagname pd-notice-box
* @summary Contextual notice box for info, note, success, warning, and error messages.
*
* @slot - Default slot for notice content.
*
* @cssprop --pd-notice-box-width - Width of the notice box. Default: `100%`.
* @cssprop --pd-notice-box-border-radius - Border radius. Default: `--pd-radius-md`.
* @cssprop --pd-notice-box-padding - Content padding. Default: `--pd-spacing-sm`.
* @cssprop --pd-notice-box-gap - Gap between icon and content. Default: `--pd-spacing-sm`.
* @cssprop --pd-notice-box-font-size - Font size. Default: `--pd-default-font-content-size`.
* @cssprop --pd-notice-box-title-font-size - Title font size. Default: `--pd-default-font-input-size`.
* @cssprop --pd-notice-box-text-col - Text color. Default: `--pd-default-font-content-col`.
* @cssprop --pd-notice-box-title-font-weight - Title font weight. Default: `600`.
* @cssprop --pd-notice-box-icon-size - Icon size. Default: `24px`.
* @cssprop --pd-notice-box-sidebar-width - Left border width for sidebar variant. Default: `4px`.
*
* Type-specific colors (with defaults from pd-shared-styles):
* @cssprop --pd-notice-box-info-col - Info type main color. Default: `--pd-default-info-col`.
* @cssprop --pd-notice-box-info-bg-col - Info type background. Default: light blue.
* @cssprop --pd-notice-box-note-col - Note type main color. Default: `--pd-default-col` (teal).
* @cssprop --pd-notice-box-note-bg-col - Note type background. Default: light teal.
* @cssprop --pd-notice-box-success-col - Success type main color. Default: `--pd-default-success-col`.
* @cssprop --pd-notice-box-success-bg-col - Success type background. Default: `--pd-default-success-light-col`.
* @cssprop --pd-notice-box-warning-col - Warning type main color. Default: `--pd-default-warning-col`.
* @cssprop --pd-notice-box-warning-bg-col - Warning type background. Default: light yellow.
* @cssprop --pd-notice-box-error-col - Error type main color. Default: `--pd-default-error-col`.
* @cssprop --pd-notice-box-error-bg-col - Error type background. Default: `--pd-default-error-light-col`.
*
* @example
* ```html
* <!-- Box variant with icon -->
* <pd-notice-box type="info" title="Information">
*   This is an informational message.
* </pd-notice-box>
*
* <!-- Sidebar variant (no icon) -->
* <pd-notice-box type="warning" variant="sidebar" title="Warning">
*   This is a warning message.
* </pd-notice-box>
*
* <!-- Compact variant (inline, small) -->
* <pd-notice-box type="note" variant="compact" icon="aiSparkleIcon">
*   AI-generated suggestion text.
* </pd-notice-box>
*
* <!-- Custom icon -->
* <pd-notice-box type="success" icon="starIcon" title="Success">
*   Operation completed!
* </pd-notice-box>
*
* <!-- Without icon -->
* <pd-notice-box type="error" hideIcon title="Error">
*   Something went wrong.
* </pd-notice-box>
* ```
*/
var PdNoticeBox = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.type = "info";
		this.variant = "box";
		this.title = "";
		this.icon = "";
		this.hideIcon = false;
	}
	static {
		this.styles = [css`
      :host {
        /* Layout */
        --_width: var(--pd-notice-box-width, 100%);
        --_radius: var(--pd-notice-box-border-radius, var(--pd-radius-lg, 8px));
        --_padding: var(--pd-notice-box-padding, var(--pd-spacing-sm, 12px));
        --_gap: var(--pd-notice-box-gap, var(--pd-spacing-sm, 12px));

        /* Typography */
        --_font-size: var(
          --pd-notice-box-font-size,
          var(--pd-default-font-content-size, 1rem)
        );
        --_title-font-size: var(
          --pd-notice-box-title-font-size,
          var(--pd-default-font-input-size, 1.05rem)
        );
        --_title-font-weight: var(--pd-notice-box-title-font-weight, 600);

        /* Icon */
        --_icon-size: var(--pd-notice-box-icon-size, 24px);

        /* Sidebar */
        --_sidebar-width: var(--pd-notice-box-sidebar-width, 4px);

        /* Type colors - will be overridden per type */
        --_type-col: var(--pd-default-info-col, #6b86ff);
        --_type-bg-col: #e8f0ff;
        --_type-text-col: var(
          --pd-notice-box-text-col,
          var(--pd-default-font-content-col, #353738)
        );

        display: block;
        width: var(--_width);
      }

      /* Type-specific color overrides */
      :host([type="info"]) {
        --_type-col: var(
          --pd-notice-box-info-col,
          var(--pd-default-info-col, #6b86ff)
        );
        --_type-bg-col: var(--pd-notice-box-info-bg-col, #e8f0ff);
      }

      :host([type="note"]) {
        --_type-col: var(
          --pd-notice-box-note-col,
          var(--pd-default-col, #067394)
        );
        --_type-bg-col: var(
          --pd-notice-box-note-bg-col,
          var(--pd-default-lightest-col, #cde2eb)
        );
      }

      :host([type="success"]) {
        --_type-col: var(
          --pd-notice-box-success-col,
          var(--pd-default-success-col, #348b11)
        );
        --_type-bg-col: var(
          --pd-notice-box-success-bg-col,
          var(--pd-default-success-light-col, #f5ffe8)
        );
      }

      :host([type="warning"]) {
        --_type-col: var(
          --pd-notice-box-warning-col,
          var(--pd-default-warning-col, #ffbf00)
        );
        --_type-bg-col: var(--pd-notice-box-warning-bg-col, #fff8e0);
      }

      :host([type="error"]) {
        --_type-col: var(
          --pd-notice-box-error-col,
          var(--pd-default-error-col, #a1141b)
        );
        --_type-bg-col: var(
          --pd-notice-box-error-bg-col,
          var(--pd-default-error-light-col, #ffe8e8)
        );
      }

      /* Box variant */
      .notice-box {
        display: flex;
        align-items: flex-start;
        gap: var(--_gap);
        padding: var(--_padding);
        border-radius: var(--_radius);
        background-color: var(--_type-bg-col);
        border: 1px solid var(--_type-col);
        font-size: var(--_font-size);
        color: var(--_type-text-col);
      }

      /* Sidebar variant */
      :host([variant="sidebar"]) .notice-box {
        background-color: transparent;
        border: none;
        border-left: var(--_sidebar-width) solid var(--_type-col);
        border-radius: 0;
        padding-left: var(--_padding);
      }

      /* Icon wrapper */
      .icon-wrapper {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--_icon-size);
        height: var(--_icon-size);
      }

      .icon-wrapper pd-icon {
        --pd-icon-primary-col: var(--_type-col);
        --pd-icon-col-primary-active: var(--_type-col);
        width: var(--_icon-size);
        height: var(--_icon-size);
      }

      /* Content area */
      .content {
        flex: 1;
        min-width: 0;
      }

      .title {
        margin: 0 0 0.25em 0;
        font-size: var(--_title-font-size);
        font-weight: var(--_title-font-weight);
        font-family: var(--pd-default-font-title-family, inherit);
        color: var(--_type-col);
        line-height: 1.3;
      }

      .body {
        line-height: 1.5;
      }

      .body ::slotted(*) {
        margin: 0;
      }

      .body ::slotted(*:not(:last-child)) {
        margin-bottom: 0.5em;
      }

      /* Compact variant – propagate sizing via CSS vars for slotted components (e.g. pd-more-info) */
      :host([variant="compact"]) {
        --pd-default-font-content-size: 0.8rem;
        --pd-default-font-content-line-height: 1.3;
        --pd-default-font-link-size: 0.75rem;
      }

      :host([variant="compact"]) .notice-box {
        background-color: color-mix(in srgb, var(--_type-col) 4%, transparent);
        border: none;
        border-left: 3px solid var(--_type-col);
        border-radius: 0;
        padding: 6px 8px;
        gap: 6px;
        font-size: 0.8rem;
        line-height: 1.3;
        align-items: center;
      }

      :host([variant="compact"]) .icon-wrapper {
        width: 22px;
        height: 22px;
      }

      :host([variant="compact"]) .icon-wrapper pd-icon {
        width: 22px;
        height: 22px;
      }

      :host([variant="compact"]) .title {
        display: inline;
        font-size: 0.8rem;
        margin: 0 4px 0 0;
      }

      :host([variant="compact"]) .body {
        line-height: 1.3;
      }
    `];
	}
	render() {
		const showIcon = (this.variant === "box" || this.variant === "compact") && !this.hideIcon;
		const iconName = this.icon || DEFAULT_ICONS[this.type];
		return html`
      <div class="notice-box" role="note" aria-label="${this.type} notice">
        ${showIcon ? html`
                <div class="icon-wrapper" aria-hidden="true">
                  <pd-icon icon="${iconName}" class="primary"></pd-icon>
                </div>
              ` : nothing}
        <div class="content">
          ${this.title ? html`<h4 class="title">${this.title}</h4>` : nothing}
          <div class="body">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
	}
};
__decorate([property({
	type: String,
	reflect: true
})], PdNoticeBox.prototype, "type", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdNoticeBox.prototype, "variant", void 0);
__decorate([property({ type: String })], PdNoticeBox.prototype, "title", void 0);
__decorate([property({ type: String })], PdNoticeBox.prototype, "icon", void 0);
__decorate([property({ type: Boolean })], PdNoticeBox.prototype, "hideIcon", void 0);
//#endregion
export { PdNoticeBox };
